import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fmtDate } from "@/lib/api";
import {
  Home, UserCircle, CalendarDays, Gift, GraduationCap, TrendingUp, FileText, Users,
  ShieldCheck, HeartPulse, Briefcase, BarChart3, Settings as SettingsIcon, Bell, LogOut,
  Search, Menu, X, LifeBuoy, Mail,
} from "lucide-react";
import { LeaveTypesProvider } from "@/context/LeaveTypesContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import HelpPanel from "@/components/HelpPanel";

const ALL = ["admin", "hr_admin", "manager", "employee"];

// Permission/role-driven navigation. Reuses existing routes only.
const NAV = [
  { to: "/", label: "Home", icon: Home, roles: ALL },
  { to: "/my-hr", label: "My Profile", icon: UserCircle, roles: ALL },
  { to: "/requests", label: "Leave & Absence", icon: CalendarDays, roles: ALL },
  { to: "/my-total-reward", label: "Benefits", icon: Gift, roles: ["employee"], module: "benefits" },
  { to: "/learning", label: "Learning", icon: GraduationCap, roles: ["employee"], module: "learning" },
  { to: "/wellbeing", label: "Wellbeing", icon: HeartPulse, roles: ALL, module: "wellbeing" },
  { to: "/help", label: "Help", icon: LifeBuoy, roles: ALL, module: "help" },
  { to: "/people/training", label: "Learning", icon: GraduationCap, roles: ["admin", "hr_admin", "manager"], module: "learning" },
  { to: "/people/performance", label: "Performance", icon: TrendingUp, roles: ["admin", "hr_admin", "manager"] },
  { to: "/people/documents", label: "Documents & Policies", icon: FileText, roles: ["admin", "hr_admin"] },
  { to: "/email-signatures", label: "Email Signatures", icon: Mail, roles: ["admin", "hr_admin"] },
  { to: "/directory", label: "Directory", icon: Users, roles: ALL, module: "directory" },
  { to: "/calendar", label: "Team calendar", icon: CalendarDays, roles: ALL },
  { section: "Management" },
  { to: "/hr-hub", label: "HR Hub", icon: ShieldCheck, roles: ["admin", "hr_admin"] },
  { to: "/employees", label: "People", icon: Users, roles: ["admin", "hr_admin", "manager"] },
  { to: "/people/cases", label: "HR Cases", icon: ShieldCheck, roles: ["admin", "hr_admin", "manager"] },
  { to: "/recruitment", label: "Recruitment", icon: Briefcase, roles: ["admin", "hr_admin", "manager"], module: "recruitment" },
  { to: "/approvals", label: "Approvals", icon: CalendarDays, roles: ["admin", "hr_admin", "manager"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "hr_admin", "manager"], module: "reports" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin"] },
];

const tid = (label) => `nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`;

function SearchBox() {
  const { user } = useAuth();
  const helpOn = user?.role === "admin" || (user?.modules?.help !== false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [helpArticles, setHelpArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef();
  const nav = useNavigate();
  useEffect(() => { if (helpOn) api.get("/help/articles").then((r) => setHelpArticles(r.data.articles || [])).catch(() => {}); }, [helpOn]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); setOpen(true); } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const term = q.trim().toLowerCase();
  const helpMatches = term.length >= 2 ? helpArticles
    .filter((a) => `${a.title} ${a.summary || ""} ${a.category} ${(a.body || []).map((s) => `${s.h} ${s.p}`).join(" ")}`.toLowerCase().includes(term))
    .slice(0, 4)
    .map((a) => ({ label: a.title, sub: a.summary, type: "Help", to: `/help?article=${encodeURIComponent(a.key || a.id)}` })) : [];
  const results = [...res, ...helpMatches];
  const go = (r) => { setOpen(false); setQ(""); setRes([]); nav(r.to); };
  return (
    <div className="relative flex-1 max-w-md hidden sm:block" ref={box}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)} placeholder="Search people, documents, reports, help…" data-testid="global-search" aria-label="Search people, documents, reports and help"
        className="w-full rounded-full bg-slate-100 border border-transparent focus:border-[#2EA3FD] focus:bg-white pl-9 pr-4 py-2 text-sm outline-none transition-colors" />
      {open && q.trim().length >= 2 && (
        <div className="absolute mt-2 w-full max-h-96 overflow-y-auto bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-50" data-testid="search-results">
          {results.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400" data-testid="search-empty">No matches.</p> :
            results.map((r, i) => (
              <button key={i} onClick={() => go(r)} data-testid={`search-result-${i}`} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center justify-between gap-2">
                <span className="min-w-0"><span className="block text-sm font-medium text-[#0B1A2F] truncate">{r.label}</span>{r.sub && <span className="block text-xs text-slate-400 truncate">{r.sub}</span>}</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">{r.type}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [logoUrl, setLogoUrl] = useState("");
  const [annSig, setAnnSig] = useState("");
  const [annCount, setAnnCount] = useState(0);
  const [annSeen, setAnnSeen] = useState(() => localStorage.getItem("cass_ann_seen") || "");
  useEffect(() => {
    api.get("/branding").then((r) => {
      setLogoUrl(r.data.logo_url || "");
      setAnnSig(r.data.announcements_sig || "");
      setAnnCount(r.data.announcements_count || 0);
    }).catch(() => {});
  }, []);
  const homeHasNew = annCount > 0 && annSig && annSig !== annSeen;
  const annToasted = useRef(false);
  useEffect(() => {
    if (!annToasted.current && annCount > 0 && annSig && annSig !== annSeen && loc.pathname !== "/") {
      annToasted.current = true;
      toast.info(`${annCount} new update${annCount !== 1 ? "s" : ""} on your Home`, {
        action: { label: "View", onClick: () => nav("/") },
      });
    }
  }, [annSig, annCount]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loc.pathname === "/" && annSig && annSig !== annSeen) {
      localStorage.setItem("cass_ann_seen", annSig);
      setAnnSeen(annSig);
    }
  }, [loc.pathname, annSig, annSeen]);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [attention, setAttention] = useState(0);
  const [menu, setMenu] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const menuRef = useRef();

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get("/notifications"); setNotifs(data); setUnread(data.filter((n) => !n.read).length); } catch { /* noop */ }
      if (["admin", "hr_admin"].includes(user?.role)) {
        try { const { data } = await api.get("/reports/hr-attention"); setAttention(data.count || 0); } catch { /* noop */ }
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [loc.pathname, user]);

  // Map a notification to where it refers to (mirrors Notifications.jsx targetFor).
  const notifTarget = (n) => {
    const focus = n.related_id ? `?focus=${n.related_id}` : "";
    return n.type === "new_request" ? `/approvals${focus}` : `/requests${focus}`;
  };
  // Opening the dropdown counts as reading them: clear the badge and mark all read server-side.
  const onNotifOpenChange = (o) => {
    setNotifOpen(o);
    if (o && unread > 0) { setUnread(0); api.post("/notifications/read-all").catch(() => {}); }
  };
  const openNotif = (n) => { setNotifOpen(false); nav(notifTarget(n)); };

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setMobileNav(false); }, [loc.pathname]);

  const modules = user?.modules || {};
  const isAdmin = user?.role === "admin";
  const moduleOn = (n) => !n.module || isAdmin || modules[n.module] !== false;
  const roleOf = (n) => n.section || n.roles.includes(user?.role);
  const items = NAV.filter((n) => roleOf(n) && (n.section || moduleOn(n)));
  // drop a section header if it has no following visible items
  const visible = items.filter((n, i) => !(n.section && !items.slice(i + 1).some((x) => !x.section)));
  const initials = (user?.name || "?").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const doLogout = async () => { await logout(); nav("/login"); };
  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const NavList = ({ onNavigate }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
      {visible.map((n, i) => n.section ? (
        <p key={`s${i}`} className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{n.section}</p>
      ) : (
        <Link key={n.to + n.label} to={n.to} onClick={onNavigate} data-testid={tid(n.label)}
          className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive(n.to) ? "bg-[#2EA3FD] text-white font-semibold shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
          <n.icon size={18} className="shrink-0" />
          <span className="truncate">{n.label}</span>
          {isAdmin && n.module && modules[n.module] === false && (
            <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300 border border-amber-400/30" data-testid={`nav-off-${n.module}`} title="Module hidden from staff — admin preview">Off</span>
          )}
          {n.to === "/" && homeHasNew && <span className="ml-auto h-2 w-2 rounded-full bg-[#2EA3FD] ring-2 ring-[#2EA3FD]/30" data-testid="home-new-dot" title="New announcement" />}
          {n.to === "/hr-hub" && attention > 0 && <span className="ml-auto inline-grid place-items-center h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold" data-testid="hr-hub-attention-badge">{attention}</span>}
        </Link>
      ))}
    </nav>
  );

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-[#0B1A2F]">
      <Link to="/" className="flex items-center gap-2 px-5 h-16 shrink-0 border-b border-white/10" data-testid="cass-logo">
        {logoUrl ? (
          <img src={logoUrl} alt="Company logo" className="max-h-9 w-auto object-contain bg-white rounded-md px-2 py-1" data-testid="sidebar-logo" />
        ) : (
          <>
            <span className="text-2xl font-extrabold tracking-tight font-display text-white">CASS</span>
            <span className="text-[11px] leading-tight text-[#8FD0FF] font-medium">Cloud Accounting<br />Support Services</span>
          </>
        )}
      </Link>
      <NavList onNavigate={() => setMobileNav(false)} />
      <div className="px-3 py-3 border-t border-white/10">
        <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors">
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <LeaveTypesProvider>
      <div className="min-h-screen bg-[#EEF2F7]">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 z-40">
          <Sidebar />
        </aside>

        {/* Mobile drawer */}
        {mobileNav && (
          <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-nav">
            <div className="w-64 max-w-[80%]"><Sidebar /></div>
            <div className="flex-1 bg-black/40" onClick={() => setMobileNav(false)} />
          </div>
        )}

        <div className="lg:pl-60">
          {/* Top utility bar */}
          <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30">
            <div className="h-full px-4 sm:px-6 flex items-center gap-3">
              <button className="lg:hidden text-[#0B1A2F]" onClick={() => setMobileNav(true)} data-testid="mobile-nav-toggle"><Menu size={22} /></button>
              <SearchBox />
              <div className="flex items-center gap-3 ml-auto">
                <span className="hidden md:block text-xs text-slate-400" data-testid="topbar-date">{today}</span>
                <HelpPanel />
                <Popover open={notifOpen} onOpenChange={onNotifOpenChange}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="notifications-bell" aria-label="Notifications" className="relative rounded-full p-2 hover:bg-slate-100 transition-colors text-slate-500">
                      <Bell size={19} />
                      {unread > 0 && <span className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] grid place-items-center font-semibold" data-testid="notifications-badge">{unread > 9 ? "9+" : unread}</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-dropdown">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                      <p className="text-sm font-semibold text-[#0B1A2F]">Notifications</p>
                      <span className="text-[11px] text-slate-400">{notifs.length ? `${notifs.length} recent` : ""}</span>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                      {notifs.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notifications-empty">You're all caught up</div>}
                      {notifs.slice(0, 8).map((n) => (
                        <button key={n.id} type="button" data-testid={`notif-dropdown-${n.id}`} onClick={() => openNotif(n)}
                          className={`w-full text-left flex items-start gap-2.5 px-4 py-3 hover:bg-slate-50 transition-colors ${!n.read ? "bg-[#EFF8FF]/50" : ""}`}>
                          {!n.read ? <span className="mt-1.5 h-2 w-2 rounded-full bg-[#2EA3FD] shrink-0" /> : <span className="mt-1.5 h-2 w-2 shrink-0" />}
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-[#0B1A2F] leading-snug">{n.message}</span>
                            <span className="block text-[11px] text-slate-400 mt-0.5">{fmtDate(n.created_at)}</span>
                          </span>
                          <ChevronRight size={15} className="text-slate-300 shrink-0 mt-0.5" />
                        </button>
                      ))}
                    </div>
                    <Link to="/notifications" onClick={() => setNotifOpen(false)} data-testid="notifications-view-all" className="block text-center text-xs font-medium text-[#2EA3FD] hover:bg-slate-50 py-3 border-t border-slate-100">View all notifications</Link>
                  </PopoverContent>
                </Popover>
                <div className="relative" ref={menuRef}>
                  <button onClick={() => setMenu((m) => !m)} data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full hover:bg-slate-100 pl-1 pr-2 py-1 transition-colors">
                    <span className="h-9 w-9 rounded-full bg-[#2EA3FD] grid place-items-center text-white text-xs font-bold">{initials}</span>
                    <span className="hidden sm:block text-sm font-medium text-[#0B1A2F] max-w-[120px] truncate">{user?.name}</span>
                  </button>
                  {menu && (
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 text-slate-700 z-50" data-testid="user-menu">
                      <div className="px-3 py-2 border-b border-slate-100">
                        <p className="text-sm font-semibold text-[#0B1A2F] truncate">{user?.name}</p>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">{user?.role}</p>
                      </div>
                      {user?.employee_id && (
                        <Link to={`/employees/${user.employee_id}`} onClick={() => setMenu(false)} data-testid="my-details-link" className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-slate-700">
                          <UserCircle size={15} /> My details
                        </Link>
                      )}
                      <button onClick={doLogout} data-testid="logout-btn-menu" className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50">
                        <LogOut size={15} /> Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
            {children}
          </main>
        </div>
      </div>
    </LeaveTypesProvider>
  );
}
